import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env, FIRST_PAGE } from '@noor/config';
import {
  bannerTypeSearchSchema,
  businessQueries,
  businessListSearchSchema,
  businessSearchToFilter,
  isBusinessFilterActive,
  type BusinessListSearch,
} from '@/entities/business';
import { BannersPage } from '@/pages/business';

const bannersSearchSchema = businessListSearchSchema.extend({
  type: bannerTypeSearchSchema,
});

export const Route = createFileRoute('/business/banners')({
  staticData: { permission: 'business.banners.view' },
  beforeLoad: () => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // TanStack Router uses redirect as a control-flow exception.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: bannersSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    // Search/status filter the whole collection client-side (see the table).
    const filtering = isBusinessFilterActive(deps);
    const filter = businessSearchToFilter(deps);
    const { queryClient } = context;
    let prefetch: Promise<void>;
    if (deps.type === 'restaurant') {
      prefetch = filtering
        ? queryClient.prefetchQuery(businessQueries.restaurantBannerAll())
        : queryClient.prefetchQuery(businessQueries.restaurantBannerList(filter));
    } else {
      prefetch = filtering
        ? queryClient.prefetchQuery(businessQueries.productBannerAll())
        : queryClient.prefetchQuery(businessQueries.productBannerList(filter));
    }
    void prefetch.catch(() => undefined);
  },
  component: BannersRoute,
});

function BannersRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onParamsChange = (next: Partial<BusinessListSearch>) => {
    const resetPage = !('page' in next) && search.page !== FIRST_PAGE;
    void navigate({
      search: (prev) => ({
        ...prev,
        ...next,
        ...(resetPage ? { page: FIRST_PAGE } : {}),
      }),
    });
  };
  return (
    <BannersPage
      params={search}
      type={search.type}
      onTypeChange={(type) =>
        void navigate({ search: (prev) => ({ ...prev, type, page: FIRST_PAGE }) })
      }
      onParamsChange={onParamsChange}
    />
  );
}
